import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { RootState } from 'core';
import mine from 'mime';

import publicContentAPI from '../api.service';
import { refreshDataAfterUpdate } from '../utils';
import { uploadedBulkIcons } from './icons.reducer';

import { DeletePublicContentParams } from '../types';
import { IErrors } from 'core/types';
import { PublicContent, PublicContentsParams, PublicContentsType } from 'modules/common/types';

import commonPublicContentAPI from 'modules/common/api.services/public-content.api.service';

export const getIcons = createAsyncThunk<
  PublicContent,
  Omit<PublicContentsParams, 'contentType'> | undefined,
  { rejectValue: IErrors }
>('manage/icons', async (payload, thunkApi) => {
  try {
    const response = await commonPublicContentAPI.getPublicContents({
      ...payload,
      contentType: PublicContentsType.Icon,
    });

    return response.data;
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const createIcon = createAsyncThunk<
  void,
  PublicContentsParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/icons/create', async ({ onSuccess, ...payload }, thunkApi) => {
  try {
    await publicContentAPI.createPublicContent({
      contentType: PublicContentsType.Icon,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getIcons(params));
    };

    refreshDataAfterUpdate({
      contentType: PublicContentsType.Icon,
      store: thunkApi.getState(),
      refreshCb,
    });

    onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const uploadArchiveIcons = createAsyncThunk<
  void,
  PublicContentsParams,
  { rejectValue: string; state: RootState }
>('manage/icons/upload/archive', async (payload, thunkApi) => {
  try {
    await publicContentAPI.uploadArchivePublicContents({
      contentType: PublicContentsType.Icon,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getIcons(params));
    };

    refreshDataAfterUpdate({
      contentType: PublicContentsType.Icon,
      store: thunkApi.getState(),
      refreshCb,
    });
  } catch (err) {
    const error = err as AxiosError<string>;
    if (!error.response) {
      throw err;
    }

    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const uploadBulkIcons = createAsyncThunk<
  void,
  PublicContentsParams[],
  { rejectValue: IErrors; state: RootState }
>('manage/icons/upload/bulk', async (payload, thunkApi) => {
  try {
    const handleSemaphoreResult = (successfullyUploaded: { name: string; mimeType: string }[]) => {
      const successfullyUploadedFiles = successfullyUploaded.map((item) => {
        const fileExtension = mine.getExtension(item.mimeType);
        return `${item.name}.${fileExtension}`;
      });

      thunkApi.dispatch(uploadedBulkIcons(successfullyUploadedFiles));

      const refreshCb = (params: PublicContentsParams) => {
        thunkApi.dispatch(getIcons(params));
      };

      refreshDataAfterUpdate({
        contentType: PublicContentsType.Icon,
        store: thunkApi.getState(),
        refreshCb,
      });
    };

    await publicContentAPI.uploadBulkPublicContents(payload, handleSemaphoreResult);
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const editIcon = createAsyncThunk<
  void,
  PublicContentsParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/icons/edit', async ({ onSuccess, ...payload }, thunkApi) => {
  try {
    await publicContentAPI.editPublicContent({
      contentType: PublicContentsType.Icon,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getIcons(params));
    };

    refreshDataAfterUpdate({
      initialState: false,
      contentType: PublicContentsType.Icon,
      store: thunkApi.getState(),
      refreshCb,
    });

    onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const editIconTags = createAsyncThunk<
  void,
  PublicContentsParams,
  { rejectValue: IErrors; state: RootState }
>('manage/icons/edit/tags', async (payload, thunkApi) => {
  try {
    await publicContentAPI.editPublicContentTags({
      contentType: PublicContentsType.Icon,
      ...payload,
    });

    // const refreshCb = (params: PublicContentsParams) => {
    //   thunkApi.dispatch(getIcons(params));
    // };

    // refreshDataAfterUpdate({
    //   initialState: false,
    //   contentType: PublicContentsType.Icon,
    //   store: thunkApi.getState(),
    //   refreshCb,
    // });
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const editBulkIconTags = createAsyncThunk<
  void,
  {
    params: PublicContentsParams[];
    onSuccess: () => void;
  },
  { rejectValue: IErrors; state: RootState }
>('manage/bulk/icons/edit/tags', async ({ params, onSuccess }, thunkApi) => {
  try {
    await publicContentAPI.editBulkPublicContentTags(params);

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getIcons(params));
    };

    refreshDataAfterUpdate({
      initialState: false,
      contentType: PublicContentsType.Icon,
      store: thunkApi.getState(),
      refreshCb,
    });

    onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const deleteIcon = createAsyncThunk<
  void,
  DeletePublicContentParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/icons/delete', async (payload, thunkApi) => {
  try {
    await publicContentAPI.deletePublicContent({
      contentType: PublicContentsType.Icon,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getIcons(params));
    };

    refreshDataAfterUpdate({
      initialState: false,
      contentType: PublicContentsType.Icon,
      store: thunkApi.getState(),
      refreshCb,
    });

    payload.onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});

export const deleteBulkIcons = createAsyncThunk<
  void,
  DeletePublicContentParams & { onSuccess: () => void },
  { rejectValue: IErrors; state: RootState }
>('manage/icons/delete/bulk', async (payload, thunkApi) => {
  try {
    await publicContentAPI.deleteBulkPublicContents({
      contentType: PublicContentsType.Icon,
      ...payload,
    });

    const refreshCb = (params: PublicContentsParams) => {
      thunkApi.dispatch(getIcons(params));
    };

    refreshDataAfterUpdate({
      contentType: PublicContentsType.Icon,
      store: thunkApi.getState(),
      refreshCb,
    });

    payload.onSuccess();
  } catch (err) {
    const error = err as AxiosError<IErrors>;
    if (!error.response) {
      throw err;
    }
    return thunkApi.rejectWithValue(error.response.data);
  }
});
